import { useState, useEffect, useMemo } from 'react';
import { useFactory } from '../../../../context/FactoryContext';
import { employeeService } from '../services/employeeService';
import {
  collectEmployeeFilterOptions,
  employeeMatchesListFilters,
} from '../utils/employeeListFilters';

const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [5, 10, 25];

export const useEmployees = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    type: '',
    department: '',
    team: '',
    supervisor: '',
  });
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const { selectedFactory: selectedFactoryFromContext } = useFactory();
  const factoryBase = selectedFactoryFromContext || '';

  // Función para cargar empleados (siempre con detalles completos)
  const loadEmployees = async (showLoader = true) => {
    try {
      if (showLoader) setLoading(true);
      setError(null);
      // Siempre cargar con detalles completos
      const data = await employeeService.getEmployees(factoryBase, true);
      setEmployees(data);
    } catch (err) {
      console.error('Error cargando empleados:', err);
      setError(err.message || 'Error al cargar los empleados');
      setEmployees([]);
    } finally {
      if (showLoader) setLoading(false);
    }
  };

  // Función para seleccionar un empleado y mostrar sus detalles
  // Los detalles ya están incluidos en los datos cargados, solo necesitamos seleccionar el empleado
  const loadEmployeeDetails = async (employeeId) => {
    if (!employeeId) {
      setSelectedEmployee(null);
      return;
    }

    // Buscar el empleado en la lista cargada (que ya tiene todos los detalles)
    const employee = employees.find(emp => emp.id === employeeId);
    if (employee) {
      setSelectedEmployee(employee);
    } else {
      console.warn('Empleado no encontrado en la lista:', employeeId);
      setSelectedEmployee(null);
    }
  };

  // Cargar empleados al montar y cuando cambie la fábrica (desde el contexto)
  useEffect(() => {
    setFilters((prev) => ({ ...prev, team: '', supervisor: '', department: '' }));
    loadEmployees();
  }, [factoryBase]);

  // Filtrado de empleados por búsqueda y filtros
  const filteredEmployees = useMemo(() => {
    let filtered = employees;
    
    // Filtro de búsqueda
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(emp => 
        `${emp.first_name || ''} ${emp.last_name || ''}`.toLowerCase().includes(query) ||
        (emp.email || '').toLowerCase().includes(query) ||
        (emp.phone || '').toLowerCase().includes(query) ||
        (emp.factory_base || '').toLowerCase().includes(query) ||
        (emp.contract_type || '').toLowerCase().includes(query)
      );
    }
    
    // Filtro de estado
    if (filters.status) {
      if (filters.status === 'active') {
        filtered = filtered.filter(emp => emp.active === true);
      } else if (filters.status === 'inactive') {
        filtered = filtered.filter(emp => emp.active === false);
      }
    }
    
    // Filtro de tipo
    if (filters.type) {
      if (filters.type === 'internal') {
        filtered = filtered.filter(emp => !emp.external);
      } else if (filters.type === 'external') {
        filtered = filtered.filter(emp => emp.external === true);
      }
    }
    
    filtered = filtered.filter((emp) => employeeMatchesListFilters(emp, filters));

    return filtered;
  }, [employees, searchQuery, filters]);

  const filterOptions = useMemo(
    () => collectEmployeeFilterOptions(employees),
    [employees]
  );

  // Lista a paginar: usar filteredEmployees
  const listToPaginate = useMemo(() => filteredEmployees, [filteredEmployees]);
  const totalItems = listToPaginate.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const employeesToShow = useMemo(
    () => listToPaginate.slice((page - 1) * pageSize, page * pageSize),
    [listToPaginate, page, pageSize]
  );
  const rangeStart = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, totalItems);

  // Reset a página 1 cuando cambie la lista o el tamaño de página
  useEffect(() => {
    setPage(1);
  }, [totalItems, pageSize]);

  const handleApplyFilters = (newFilters) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  return {
    employees,
    loading,
    error,
    page,
    setPage,
    pageSize,
    setPageSize,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    searchQuery,
    setSearchQuery,
    employeesToShow,
    totalItems,
    totalPages,
    rangeStart,
    rangeEnd,
    loadEmployees,
    selectedEmployee,
    setSelectedEmployee,
    loadEmployeeDetails,
    filters,
    setFilters,
    filterOptions,
    showFiltersModal,
    setShowFiltersModal,
    handleApplyFilters
  };
};